import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  type ChameleonApproveData,
  type ChameleonPollData,
  ChameleonPollStatus,
  ChameleonState,
  ChatState,
  type MessageResponse
} from '@/models/chat';
import { DEFAULT_CHAT_BG, DEFAULT_CHAT_COLOR_1, DEFAULT_CHAT_COLOR_2 } from '@/utils/chat-utils';

export const useChatStore = defineStore('chat', () => {
  const router = useRouter();
  const { t } = useI18n();
  const localePath = useLocalePath();

  const isAiMode = ref<boolean>(useRoute().path === localePath('ai-mode'));
  const isChatTermsAccepted = ref<boolean>(false);
  const isChatLeaving = ref<boolean>(false);
  const isChatLeaveGranted = ref<boolean>(false);
  const chatState = ref<ChatState>(ChatState.Empty);

  const chameleonState = ref<ChameleonState>(ChameleonState.Idle);
  const isChameleonMode = computed(() => chameleonState.value === ChameleonState.Accepted);
  const channelId = ref<string>();
  const searchCount = ref(0);
  const isLastSearch = computed(() => searchCount.value >= 2);

  const chatColor1 = ref<string>(DEFAULT_CHAT_COLOR_1);
  const chatColor2 = ref<string>(DEFAULT_CHAT_COLOR_2);
  const chatBgImage = ref<string>();
  const { executeRecaptcha } = useGoogleRecaptcha();

  const polling = ref<ReturnType<typeof setInterval>>();
  const pollingInterval = 3000;

  const messages = ref<MessageResponse[]>([]);
  const currentFoundMessage = ref<MessageResponse>();
  const foundCompanyName = ref<string>();
  const isCompanyUrlModalOpen = ref(false);
  const isSearchDisabled = computed(
    () =>
      chameleonState.value === ChameleonState.Declined ||
      chameleonState.value === ChameleonState.Accepted ||
      chameleonState.value === ChameleonState.Found ||
      !channelId.value
  );

  const resetChat = () => {
    foundCompanyName.value = undefined;
    channelId.value = undefined;
    isAiMode.value = false;
    isChatLeaveGranted.value = false;

    chatState.value = ChatState.Empty;
    chameleonState.value = ChameleonState.Idle;
    searchCount.value = 0;
    currentFoundMessage.value = undefined;

    stopPolling();
    closeCompanyUrlModal();
    turnChameleonModeOff();
  };

  const setChannelId = (id?: string) => {
    channelId.value = id;
  };

  const acceptTerms = () => {
    isChatTermsAccepted.value = true;
  };

  const declineTerms = async () => {
    isChatTermsAccepted.value = false;
    isAiMode.value = false;
    isChatLeaveGranted.value = true;

    await router.replace(localePath('/'));
  };

  const searchByCompanyName = async (companyName: string) => {
    if (isSearchDisabled.value || !companyName.length) return;

    const endpoint = '/comind/company/search';
    try {
      const { recaptchaData } = await executeRecaptcha('company_search');

      await $api(endpoint, {
        method: 'POST',
        body: {
          channelId: channelId.value,
          companyName: companyName,
          ...recaptchaData
        }
      });

      chameleonState.value = ChameleonState.Searching;
      startPolling();
    } catch {
      addErrorMessage(() => searchByCompanyName(companyName));
    }
  };

  const searchByCompanyUrl = async (companyUrl: string) => {
    if (isSearchDisabled.value || !companyUrl.length) return;

    const endpoint = '/comind/company/search';

    try {
      const { recaptchaData } = await executeRecaptcha('company_search');
      await $api(endpoint, {
        method: 'POST',
        body: {
          channelId: channelId.value,
          companyUrl: companyUrl,
          ...recaptchaData
        }
      });

      chameleonState.value = ChameleonState.Searching;
      startPolling();
    } catch {
      addErrorMessage(() => searchByCompanyUrl(companyUrl));
    }
  };

  const searchByChatMessage = async (content: string, nonce?: string) => {
    if (isSearchDisabled.value || !content.length) return;

    const endpoint = '/comind/message';

    try {
      const { recaptchaData } = await executeRecaptcha('message_search');
      await $api(endpoint, {
        method: 'POST',
        body: {
          channelId: channelId.value,
          msg_content: content,
          message_nonce: nonce,
          ...recaptchaData
        }
      });

      startPolling();
    } catch {
      // do nothing
    }
  };

  const fetchChatSkin = async () => {
    if (!channelId.value) return false;

    const endpoint = `/chat-skin/status?channelId=${channelId.value}`;

    try {
      const data = await $api<ChameleonPollData>(endpoint);

      // still searching, continue to poll
      if (data.status === ChameleonPollStatus.Searching) return true;

      // website not found, nothing to do TODO: notification?
      if (data.status === ChameleonPollStatus.NotFound) return false;

      // we found something, add the validation message to chat
      addFoundMessage(data);

      // increase search count
      searchCount.value++;
    } catch {
      // do nothing
    }

    return false;
  };

  const addFoundMessage = (data: ChameleonPollData) => {
    if (!channelId.value) return;

    chameleonState.value = ChameleonState.Found;
    foundCompanyName.value = data.company;

    const ogData = JSON.parse(data.openGraphData);

    currentFoundMessage.value = {
      id: 'chameleon-approve',
      channel_id: channelId.value,
      author: { id: 'chameleon-approve' },
      content: t('chat.chameleonMessage.text'),
      timestamp: new Date().toISOString(),
      event: 'MESSAGE_UPDATE',
      chameleonApprove: true,
      chameleonUrl: data.companyUrl,
      chameleonTitle: ogData['og:title'] ?? '-',
      chameleonImage: ogData['og:image'] ?? null,
      chameleonDescription: ogData['og:description'] ?? '-'
    };

    messages.value.push(currentFoundMessage.value);
  };

  const startPolling = () => {
    stopPolling();

    polling.value = setInterval(async () => {
      const continueToPoll = await fetchChatSkin();
      if (!continueToPoll) stopPolling();
    }, pollingInterval);
  };

  const stopPolling = () => {
    clearInterval(polling.value);
    polling.value = undefined;
  };

  const acceptWebsite = async () => {
    if (!channelId.value) return;

    const endpoint = `/chat-skin/approve`;

    try {
      const { recaptchaData } = await executeRecaptcha('company_search');

      const data = await $api<ChameleonApproveData>(endpoint, {
        method: 'PATCH',
        body: {
          channelId: channelId.value,
          ...recaptchaData
        }
      });

      turnChameleonModeOn(data.primaryColor, data.secondaryColor, data.teaorImage?.src ?? DEFAULT_CHAT_BG);

      chameleonState.value = ChameleonState.Accepted;
    } catch {
      addErrorMessage(() => acceptWebsite());
    }
  };

  const declineWebsite = async () => {
    if (!channelId.value) return;

    chameleonState.value = ChameleonState.Idle;

    if (currentFoundMessage.value) {
      currentFoundMessage.value.chameleonDeclined = true;
    }

    if (searchCount.value >= 2) {
      chameleonState.value = ChameleonState.Declined;
      return;
    }

    const endpoint = `/chat-skin/decline`;

    try {
      const { recaptchaData } = await executeRecaptcha('company_search');

      await $api<ChameleonApproveData>(endpoint, {
        method: 'PATCH',
        body: {
          channelId: channelId.value,
          ...recaptchaData
        }
      });

      openCompanyUrlModal();
    } catch {
      addErrorMessage(() => declineWebsite());
    }
  };

  const turnChameleonModeOn = (color1: string, color2: string, bgImage: string) => {
    chatColor1.value = color1;
    chatColor2.value = color2;
    chatBgImage.value = bgImage;
  };

  const turnChameleonModeOff = () => {
    chatColor1.value = DEFAULT_CHAT_COLOR_1;
    chatColor2.value = DEFAULT_CHAT_COLOR_2;
    chatBgImage.value = undefined;
  };

  const openCompanyUrlModal = () => {
    isCompanyUrlModalOpen.value = true;
  };

  const closeCompanyUrlModal = () => {
    isCompanyUrlModalOpen.value = false;
  };

  const addErrorMessage = (retryFn: () => void) => {
    if (!channelId.value) return;

    messages.value.push({
      id: 'chameleon-error',
      channel_id: channelId.value,
      author: { id: 'chameleon-error' },
      content: t('chat.chameleonError.text'),
      timestamp: new Date().toISOString(),
      event: 'MESSAGE_UPDATE',
      chameleonApprove: false,
      chameleonError: true,
      chameleonErrorRetryFn: retryFn
    });
  };

  return {
    isAiMode,
    isChatTermsAccepted,
    isChatLeaving,
    isChatLeaveGranted,
    chatState,
    chameleonState,
    isChameleonMode,
    chatColor1,
    chatColor2,
    chatBgImage,
    messages,
    foundCompanyName,
    isCompanyUrlModalOpen,
    isLastSearch,
    setChannelId,
    acceptTerms,
    declineTerms,
    searchByCompanyName,
    searchByCompanyUrl,
    searchByChatMessage,
    startPolling,
    stopPolling,
    acceptWebsite,
    declineWebsite,
    turnChameleonModeOn,
    turnChameleonModeOff,
    resetChat,
    openCompanyUrlModal,
    closeCompanyUrlModal
  };
});
